import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './style.module.css';

const MultiSelectBoxAutobook = forwardRef(
  (
    {
      title,
      placeholder,
      value,
      onChange,
      options,
      AddOption = false,
      onCreateOption,
      Err = 'Incorrect Container number',
      width,
      minWidth,
      required,
      onCreateOptionContainer,
      font = false,
      fontSize,
      dropdown = false,
      height = false,
      mini,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [justAddedCustom, setJustAddedCustom] = useState(false);
    const [dropdownPos, setDropdownPos] = useState({
      top: 0,
      left: 0,
      width: null,
    });
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const [optionClicked, setOptionClicked] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState([]); // Temporary selections
    const smallDevice = window.innerWidth < 1440;
    const mediumDevice = window.innerWidth < 1650;
    const largeDevice = window.innerWidth > 1650;
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Define selectedValues (final values that are added to the list)
    const selectedValues = Array.isArray(value) ? value : [];

    // Add selected options to the final list
    const addToFinalList = () => {
      let newValues = [...selectedValues];
      let added = false;
      let invalid = false;
      // Add selected options
      if (selectedOptions.length > 0) {
        newValues = [...newValues, ...selectedOptions];
        added = true;
      }
      // Always check all comma-separated parts (even if only one, or empty strings)
      if (searchTerm.trim()) {
        const parts = searchTerm.split(',').map((s) => s.trim());
        for (let part of parts) {
          if (part.length === 11) {
            const valObj = { label: part, value: part };
            if (!newValues.some((v) => v.value === part)) {
              newValues.push(valObj);
              added = true;
            }
          } else if (part.length > 0) {
            invalid = true;
          }
        }
      }
      if (added) {
        console.log('Final newValues:', newValues);
        onChange(newValues);
        setSelectedOptions([]); // Clear temporary selections
      }
      setSearchTerm(''); // Always clear input
      if (invalid) {
        setError('Container number must be exactly 11 characters.');
      } else {
        setError('');
      }
    };

    // Expose methods to parent component
    useImperativeHandle(
      ref,
      () => ({
        getSelectedOptions: () => selectedOptions,
        addSelectedToFinalList: addToFinalList,
        getSearchTerm: () => searchTerm,
        getFinalList: () => selectedValues,
        updateFinalList: (newList) => onChange(newList),
      }),
      [selectedOptions, selectedValues, searchTerm]
    );

    // Handle clicking outside to close dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          isOpen &&
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target)
        ) {
          // Check if click is not on the dropdown options
          const dropdownElement = document.querySelector(`.${styles.options}`);
          if (!dropdownElement || !dropdownElement.contains(event.target)) {
            setIsOpen(false);
          }
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    // Calculate dropdown position when it opens or when content changes
    useEffect(() => {
      if (isOpen && wrapperRef.current) {
        const updatePosition = () => {
          const rect = wrapperRef.current.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const scrollLeft =
            window.pageXOffset || document.documentElement.scrollLeft;

          setDropdownPos({
            top: rect.bottom + scrollTop,
            left: rect.left + scrollLeft,
            width: rect.width,
          });

          // Make dropdown visible only after position is calculated
          // Small delay to ensure positioning is stable
          setTimeout(() => setDropdownVisible(true), 10);
        };

        // Use requestAnimationFrame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          updatePosition();
        });

        // Also update after a short delay to handle any layout changes
        const timeoutId = setTimeout(updatePosition, 50);

        return () => clearTimeout(timeoutId);
      } else {
        setDropdownVisible(false);
      }
    }, [isOpen, selectedValues, selectedOptions]); // Recalculate when any relevant state changes

    const displayValue = () => {
      // Only show what you're currently typing, not the final list
      return searchTerm;
    };

    const handleInputChange = (e) => {
      let newValue = e.target.value;
      // Remove all spaces (even if pasted)
      newValue = newValue.replace(/\s+/g, '');
      setSearchTerm(newValue);
      setError('');
    };

    // Handle paste event to remove spaces
    useEffect(() => {
      if (!inputRef.current) return;
      const handlePaste = (e) => {
        let pasted = (e.clipboardData || window.clipboardData).getData('text');
        if (pasted.includes(' ')) {
          e.preventDefault();
          pasted = pasted.replace(/\s+/g, '');
          // Insert cleaned text at cursor
          const input = inputRef.current;
          const start = input.selectionStart;
          const end = input.selectionEnd;
          const value = input.value;
          const newValue = value.slice(0, start) + pasted + value.slice(end);
          setSearchTerm(newValue);
          setTimeout(() => {
            input.setSelectionRange(
              start + pasted.length,
              start + pasted.length
            );
          }, 0);
        }
      };
      const input = inputRef.current;
      input.addEventListener('paste', handlePaste);
      return () => input.removeEventListener('paste', handlePaste);
    }, []);

    // Function to check if an option is selected (temporarily)
    const isSelected = (opt) =>
      selectedOptions.some((v) => v.value === opt.value);

    // Handle option selection (temporary)
    const handleOptionClick = (opt) => {
      setOptionClicked(true);
      if (isSelected(opt)) {
        const newSelectedOptions = selectedOptions.filter(
          (v) => v.value !== opt.value
        );
        setSelectedOptions(newSelectedOptions);
      } else {
        const valObj = {
          label: opt?.label ? opt.label : opt,
          value: opt?.value ? opt.value : opt,
        };
        setSelectedOptions([...selectedOptions, valObj]);
      }
      setTimeout(() => setOptionClicked(false), 100);
    };

    // Commit option to the value list
    const commitOption = (opt, suppressClose = false) => {
      const valObj = {
        label: opt?.label ? opt.label : opt,
        value: opt?.value ? opt.value : opt,
      };
      const next = [...selectedValues, valObj];
      onChange(next);
      AddOption && onCreateOption(opt?.label || opt);
      setJustAddedCustom(true);
      setSearchTerm('');
      setError('');
      if (!suppressClose) setIsOpen(false);
    };

    // Handle the blur event (when the input loses focus)
    const handleBlur = () => {
      if (!onCreateOption && options?.length === 0) {
        setSearchTerm('');
      }

      if (searchTerm?.length > 0 && searchTerm?.length < 11 && AddOption) {
        setError(Err);
      }

      if (onCreateOptionContainer && options?.length === 0) {
        onCreateOptionContainer(searchTerm);
        setIsOpen(false);
      }
    };

    // Handle focus event (clear error message)
    const handleFocus = () => {
      setError('');
    };

    // Handle Enter key to add manually typed value
    const handleKeyDown = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Don't auto-add, let user control via "Add to List" button
      }
    };

    // Render dropdown options
    const renderDropdown = () => {
      if (!isOpen || !options || options.length === 0 || !dropdownVisible)
        return null;

      // Filter out options that are already in the final list
      const availableOptions = options.filter(
        (opt) =>
          !selectedValues.some((selected) => selected.value === opt.value)
      );

      // Filter options based on search term
      const filteredOptions = dropdownSearchTerm
        ? availableOptions.filter((opt) =>
            opt?.label
              ?.toLowerCase()
              ?.includes(dropdownSearchTerm?.toLowerCase())
          )
        : availableOptions;

      return createPortal(
        <div
          className={`${styles.options} ${styles.visible} ${
            filteredOptions?.length > 6 ? styles.scrollOptions : ''
          }`}
          style={{
            position: 'absolute',
            top: dropdownPos.top + (mini ? -10 : +5),
            left: dropdownPos.left,
            width: dropdownPos.width,
            minHeight: '200px', // Keep consistent height
          }}
          onClick={(e) => e.stopPropagation()}>
          {/* Search Input - Fixed at top */}
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search containers..."
              value={dropdownSearchTerm}
              onChange={(e) => setDropdownSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options Container - Scrollable */}
          <div className={styles.optionsContainer}>
            {filteredOptions?.length > 0 ? (
              filteredOptions?.map((opt) =>
                opt?.value === '' ? null : (
                  <div
                    key={opt?.value}
                    className={`${styles.option} ${
                      isSelected(opt) ? styles.selected : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOptionClick(opt);
                    }}>
                    <span
                      className={`${styles.customCheckbox} ${
                        isSelected(opt) ? styles.checked : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOptionClick(opt);
                      }}
                      style={{ marginRight: '8px' }}>
                      {isSelected(opt) ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          style={{ display: 'block' }}>
                          <rect width="16" height="16" rx="4" fill="#2563eb" />
                          <path
                            d="M4.5 8L7 10.5L11.5 5.5"
                            stroke="white"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          style={{ display: 'block' }}>
                          <rect
                            width="16"
                            height="16"
                            rx="4"
                            fill="#fff"
                            stroke="transparent"
                            strokeWidth="2"
                          />
                        </svg>
                      )}
                    </span>
                    {opt?.label}
                  </div>
                )
              )
            ) : (
              <div
                className={styles.option}
                style={{ color: '#9ca3af', fontStyle: 'italic' }}>
                {dropdownSearchTerm
                  ? 'No options found'
                  : 'All options have been selected'}
              </div>
            )}
          </div>
        </div>,
        document.body
      );
    };

    return (
      <div className={styles.wrapper} ref={wrapperRef} style={{ width }}>
        {title && (
          <span className={font ? styles.biglabel : styles.label}>
            {title}
            {required && <span className={styles.requiredField}>*</span>}
            {required && <span className={styles.requiredText}>Required</span>}
          </span>
        )}
        <div
          className={`${font ? styles.selectBoxFont : styles.selectBox} ${
            isOpen ? styles.focused : ''
          } ${error ? styles.error : ''}`}
          onClick={() => {
            if (!isOpen) {
              setDropdownVisible(false); // Reset visibility when opening
            }
            setIsOpen(!isOpen);
            !onCreateOption && setSearchTerm('');
          }}>
          <input
            ref={inputRef}
            type="text"
            className={font ? styles.bigInput : styles.input}
            placeholder={
              selectedOptions.length > 0
                ? `${selectedOptions.length} selected`
                : placeholder
            }
            value={displayValue()}
            onChange={handleInputChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            style={{ fontSize: fontSize }}
          />
          {options?.length > 0 && <span className={styles.arrow}></span>}
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}

        {renderDropdown()}
      </div>
    );
  }
);

export default MultiSelectBoxAutobook;
